import {
  ClusterColor,
  clusterLeavesLayer,
  clusterOverlay,
  clustersIconLayer,
  sensorIconLabelLayer,
  sensorIconLayer,
} from '@components/map/layers/sensors/sensor.layer';
import { useMapContext } from '@components/map/MapContext';
import { createSensorLayerErrorToast } from '@components/ui/toaster/toaster';
import { ClusterProps, useClusterSelect } from '@hooks/useClusterSelect';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectSensorIconFeatures, selectSensorLabelFeatures } from '@redux/situation/monitoring.selectors';
import { isFeatureCollection } from '@utils/map/map.utils';
import { isNeutral } from '@utils/sensors/neutral/neutral.utils';
import { FeatureCollection, GeoJsonProperties } from 'geojson';
import { ErrorBoundary } from 'react-error-boundary';
import { Layer, Source } from 'react-map-gl/mapbox';
import Supercluster, { PointFeature } from 'supercluster';

import { MapIdEnum, SourceNameEnum } from '@/types/map.types';
import { ConnectorStatusEnum, HealthStatusEnum, SpecificStateEnum } from '@/types/sensor/sensors';

function SensorLayerContent() {
  const sensorIconFeatures = useSelectorWithReplayMode(selectSensorIconFeatures);
  const sensorLabelFeatures = useSelectorWithReplayMode(selectSensorLabelFeatures);

  const pointFeatures: PointFeature<GeoJsonProperties>[] = sensorIconFeatures.filter(
    (feature): feature is PointFeature<GeoJsonProperties> => feature.geometry.type === 'Point',
  );

  const superclusterOptions: Supercluster.Options<GeoJsonProperties, ClusterProps> = {
    radius: 52,
    minZoom: 0,
    maxZoom: 23,
    minPoints: 2,
    map: (props) => {
      if (!props)
        return {
          [ClusterColor.PURPLE]: 0,
          [ClusterColor.ORANGE]: 0,
          [ClusterColor.RED]: 0,
          [ClusterColor.GREEN]: 0,
          [ClusterColor.ORANGE_MAINTENANCE]: 0,
          [ClusterColor.GRAY]: 0,
          [ClusterColor.WHITE]: 0,
        };
      const { status, maintenance, active, type, specificState } = props;

      const isRedEmitting = isNeutral(type) && specificState === SpecificStateEnum.EMITTING;
      const isEffraction = specificState === SpecificStateEnum.EFFRACTION;

      return {
        [ClusterColor.PURPLE]:
          active && !maintenance && [ConnectorStatusEnum.UNREACHABLE, HealthStatusEnum.NA, null].includes(status)
            ? 1
            : 0,
        [ClusterColor.ORANGE]:
          active && !maintenance && [HealthStatusEnum.DEGRADED, HealthStatusEnum.KO].includes(status) ? 1 : 0,
        [ClusterColor.RED]: active && !maintenance && (isEffraction || isRedEmitting) ? 1 : 0,
        [ClusterColor.GREEN]: active && !maintenance && status === HealthStatusEnum.HEALTHY ? 1 : 0,
        [ClusterColor.ORANGE_MAINTENANCE]: active && maintenance ? 1 : 0,
        [ClusterColor.GRAY]: active && !maintenance && specificState === SpecificStateEnum.SLEEP ? 1 : 0,
        [ClusterColor.WHITE]: active === false ? 1 : 0,
      };
    },
    reduce: (accumulated: ClusterProps | null, props: Partial<ClusterProps> | null) => {
      if (!accumulated || !props) return;
      for (const key in props) {
        if (Object.hasOwn(props, key)) {
          const typedKey = key as keyof ClusterProps;
          accumulated[typedKey] = (accumulated[typedKey] || 0) + (props[typedKey] ?? 0);
        }
      }
      return accumulated;
    },
  };

  const [clusterState, leaveIds, clusters] = useClusterSelect(pointFeatures, superclusterOptions);

  const sensorFeatures: FeatureCollection = {
    type: 'FeatureCollection',
    features: clusterState.updatedClusters.length > 0 ? clusterState.updatedClusters : clusters,
  };

  if (!isFeatureCollection(sensorFeatures)) return null;

  return (
    <>
      <Source
        id={SourceNameEnum.SENSORS}
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: clusterState.selectedCluster?.id
            ? sensorFeatures.features.filter((feature) => clusterState.selectedCluster?.id !== feature.id)
            : sensorFeatures.features,
        }}
        tolerance={0}
        maxzoom={23}
        cluster
      >
        <Layer {...clustersIconLayer} />
        <Layer {...sensorIconLayer} />
      </Source>
      <Source id={SourceNameEnum.SENSORS_OVERLAY} type="geojson" data={sensorFeatures} tolerance={0} maxzoom={23}>
        <Layer {...clusterOverlay} />
        <Layer {...clusterLeavesLayer} />
      </Source>
      <Source
        id={SourceNameEnum.SENSOR_LABELS}
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: sensorLabelFeatures.filter((feature) => !leaveIds.includes(feature.properties?.id)),
        }}
      >
        <Layer {...sensorIconLabelLayer} />
      </Source>
    </>
  );
}

export default function SensorLayer() {
  const { mapId } = useMapContext();
  return mapId === MapIdEnum.REPLAY ? (
    <ErrorBoundary fallback={<></>} onError={() => createSensorLayerErrorToast(SourceNameEnum.SENSORS)}>
      <SensorLayerContent />
    </ErrorBoundary>
  ) : (
    <SensorLayerContent />
  );
}
